import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Plus, User, Phone, Mail, Edit2, Trash2, AlertTriangle, Send, Check, X, Shield, ShieldCheck, Calculator } from 'lucide-react';

const ROLE_INFO = {
  OWNER: { label: 'Owner', icon: ShieldCheck, color: 'text-purple-600', bg: 'bg-purple-100' },
  ADMIN: { label: 'Admin', icon: Shield, color: 'text-blue-600', bg: 'bg-blue-100' },
  ACCOUNTANT: { label: 'Accountant', icon: Calculator, color: 'text-green-600', bg: 'bg-green-100' }
};

function ConfirmDeleteModal({ name, type, onConfirm, onClose }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-red-900">Permanently Delete {type}</h2>
              <p className="text-sm text-red-700">This action cannot be undone</p>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-700">
            You are about to permanently delete <strong>{name}</strong>.
          </p>
          <div>
            <label className="label">Type DELETE to confirm</label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              className="input font-mono"
              placeholder="DELETE"
              autoFocus
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleConfirm}
              disabled={confirmText !== 'DELETE' || deleting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </button>
            <button onClick={onClose} className="btn-secondary flex-1" disabled={deleting}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InviteModal({ admin, onClose }) {
  const [method, setMethod] = useState('copy');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [magicLink, setMagicLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const hasPhone = !!admin.phone;
  const hasEmail = !!admin.email;

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      if (method === 'copy') {
        const response = await api.post('/auth/generate-link', {
          userId: admin.id
        });
        setMagicLink(response.magicLink);
        setResult({ success: true, copyOnly: true });
      } else {
        const response = await api.post('/auth/invite', {
          userId: admin.id,
          method
        });
        setResult({ success: true, ...response });
      }
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setSending(false);
  };

  const handleCopy = async () => {
    if (magicLink) {
      await navigator.clipboard.writeText(magicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Invite {admin.name}</h2>
          <p className="text-sm text-gray-500">Send a login link to access Hubtractor</p>
        </div>

        <div className="p-4 space-y-4">
          {result ? (
            <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-3">
                {result.success ? (
                  <Check className="w-6 h-6 text-green-600" />
                ) : (
                  <X className="w-6 h-6 text-red-600" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${result.success ? 'text-green-900' : 'text-red-900'}`}>
                    {result.success ? (result.copyOnly ? 'Link Generated!' : 'Invite Sent!') : 'Failed to Send'}
                  </p>
                  {result.success && result.sent && (
                    <div className="text-sm text-green-700 mt-1">
                      {result.sent.map((s, i) => (
                        <p key={i}>✓ Sent via {s.method} to {s.to}</p>
                      ))}
                    </div>
                  )}
                  {result.error && (
                    <p className="text-sm text-red-700 mt-1">{result.error}</p>
                  )}
                </div>
              </div>
              {magicLink && (
                <div className="mt-3 p-3 bg-white rounded border">
                  <p className="text-xs text-gray-500 mb-2">Login link (expires in 15 min):</p>
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={magicLink} 
                      readOnly 
                      className="input text-xs flex-1 bg-gray-50"
                    />
                    <button 
                      onClick={handleCopy}
                      className="btn-primary text-sm px-3"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Send login link via:</p>
                
                <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  method === 'copy' ? 'border-hub-500 bg-hub-50' : 'hover:bg-gray-50'
                }`}>
                  <input
                    type="radio"
                    name="method"
                    value="copy"
                    checked={method === 'copy'}
                    onChange={() => setMethod('copy')}
                    className="text-hub-600"
                  />
                  <div className="flex-1">
                    <p className="font-medium">Copy Link</p>
                    <p className="text-xs text-gray-500">Generate link and copy to clipboard</p>
                  </div>
                </label>
                
                <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  method === 'both' ? 'border-hub-500 bg-hub-50' : 'hover:bg-gray-50'
                } ${(!hasPhone || !hasEmail) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="method"
                    value="both"
                    checked={method === 'both'}
                    onChange={() => setMethod('both')}
                    disabled={!hasPhone || !hasEmail}
                    className="text-hub-600"
                  />
                  <div className="flex-1">
                    <p className="font-medium">Both SMS & Email</p>
                    <p className="text-xs text-gray-500">Send to phone and email</p>
                  </div>
                </label>

                <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  method === 'sms' ? 'border-hub-500 bg-hub-50' : 'hover:bg-gray-50'
                } ${!hasPhone ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="method"
                    value="sms"
                    checked={method === 'sms'}
                    onChange={() => setMethod('sms')}
                    disabled={!hasPhone}
                    className="text-hub-600"
                  />
                  <div className="flex-1">
                    <p className="font-medium">SMS Only</p>
                    <p className="text-xs text-gray-500">{admin.phone || 'No phone number'}</p>
                  </div>
                  <Phone className="w-4 h-4 text-gray-400" />
                </label>

                <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  method === 'email' ? 'border-hub-500 bg-hub-50' : 'hover:bg-gray-50'
                } ${!hasEmail ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="method"
                    value="email"
                    checked={method === 'email'}
                    onChange={() => setMethod('email')}
                    disabled={!hasEmail}
                    className="text-hub-600"
                  />
                  <div className="flex-1">
                    <p className="font-medium">Email Only</p>
                    <p className="text-xs text-gray-500">{admin.email || 'No email'}</p>
                  </div>
                  <Mail className="w-4 h-4 text-gray-400" />
                </label>
              </div>

              <p className="text-xs text-gray-500">
                The link will expire in 15 minutes.
              </p>
            </>
          )}

          <div className="flex gap-3 pt-2">
            {result ? (
              <button onClick={onClose} className="btn-primary flex-1">
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Invite
                    </>
                  )}
                </button>
                <button onClick={onClose} className="btn-secondary flex-1" disabled={sending}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminModal({ admin, currentUserId, onSave, onClose }) {
  const [name, setName] = useState(admin?.name || '');
  const [email, setEmail] = useState(admin?.email || '');
  const [phone, setPhone] = useState(admin?.phone || '');
  const [role, setRole] = useState(admin?.role || 'ADMIN');
  const [active, setActive] = useState(admin?.active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditingSelf = admin?.id === currentUserId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (admin?.id) {
        await api.put(`/admin-users/${admin.id}`, { name, email, phone, role, active });
      } else {
        await api.post('/admin-users', { name, email, phone, role });
      }
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save admin');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">{admin?.id ? 'Edit Admin' : 'New Admin'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}
          <div>
            <label className="label">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" required />
          </div>
          <div>
            <label className="label">Email *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input" required />
          </div>
          <div>
            <label className="label">Phone</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} className="input" placeholder="+1..." />
          </div>
          <div>
            <label className="label">Role *</label>
            <select 
              value={role} 
              onChange={e => setRole(e.target.value)} 
              className="input"
              disabled={isEditingSelf}
            >
              <option value="ADMIN">Admin</option>
              <option value="ACCOUNTANT">Accountant</option>
              <option value="OWNER">Owner</option>
            </select>
            {isEditingSelf && (
              <p className="text-xs text-gray-500 mt-1">You cannot change your own role</p>
            )}
          </div>
          {admin?.id && (
            <label className={`flex items-center gap-2 ${isEditingSelf ? 'opacity-50' : ''}`}>
              <input 
                type="checkbox" 
                checked={active} 
                onChange={e => setActive(e.target.checked)} 
                className="rounded"
                disabled={isEditingSelf}
              />
              <span className="text-sm">Active</span>
              {isEditingSelf && <span className="text-xs text-gray-500">(cannot deactivate yourself)</span>}
            </label>
          )}
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [inviteModal, setInviteModal] = useState(null);

  const isOwner = user?.role === 'OWNER';

  const loadAdmins = () => {
    api.get('/admin-users').then(setAdmins).finally(() => setLoading(false));
  };

  useEffect(() => { loadAdmins(); }, []);

  const deactivateAdmin = async (id, name) => {
    if (id === user?.id) {
      alert("You cannot deactivate yourself");
      return;
    }
    if (!confirm(`Deactivate ${name}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/admin-users/${id}`);
      loadAdmins();
    } catch (err) {
      alert(err.message);
    }
  };

  const permanentlyDeleteAdmin = async (id) => {
    if (id === user?.id) {
      alert("You cannot delete yourself");
      setDeleteModal(null);
      return;
    }
    try {
      await api.delete(`/admin-users/${id}/permanent`, { confirm: 'DELETE' });
      setDeleteModal(null);
      loadAdmins();
    } catch (err) {
      alert(err.message);
      setDeleteModal(null);
    }
  };

  // Only Owner can access this page
  if (!isOwner) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-600">Access Restricted</h2>
        <p className="text-gray-500">Only owners can manage admin users.</p>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Admin Users</h1>
        <button onClick={() => setModal({})} className="btn-primary"><Plus className="w-4 h-4" />Add Admin</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {admins.map(a => {
          const roleInfo = ROLE_INFO[a.role] || ROLE_INFO.ADMIN;
          const RoleIcon = roleInfo.icon;
          const isSelf = a.id === user?.id;
          
          return (
            <div key={a.id} className={`card ${isSelf ? 'ring-2 ring-hub-500' : ''}`}>
              <div className="card-body">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${roleInfo.bg} rounded-full flex items-center justify-center`}>
                      <RoleIcon className={`w-5 h-5 ${roleInfo.color}`} />
                    </div>
                    <div>
                      <h3 className="font-medium">
                        {a.name}
                        {isSelf && <span className="text-xs text-hub-600 ml-2">(You)</span>}
                      </h3>
                      <p className={`text-sm ${roleInfo.color}`}>{roleInfo.label}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setModal(a)} className="btn-ghost p-2" title="Edit"><Edit2 className="w-4 h-4" /></button>
                    {!isSelf && (
                      <button onClick={() => deactivateAdmin(a.id, a.name)} className="btn-ghost p-2 text-red-600 hover:bg-red-50" title="Deactivate"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{a.email}</span>
                  </div>
                  {a.phone && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Phone className="w-4 h-4" />
                      <span>{a.phone}</span>
                    </div>
                  )}
                </div>
                
                {!a.active && <span className="badge-cancelled mt-2">Inactive</span>}

                {/* Invite Button */}
                {a.active && !isSelf && (
                  <button
                    onClick={() => setInviteModal(a)}
                    className="mt-4 w-full btn-secondary flex items-center justify-center gap-2 text-sm"
                  >
                    <Send className="w-4 h-4" />
                    Send Login Invite
                  </button>
                )}
                
                {/* Permanent Delete - Owner Only, not self */}
                {!isSelf && (
                  <div className="mt-3 pt-3 border-t">
                    <button
                      onClick={() => setDeleteModal(a)}
                      className="text-xs text-red-600 hover:text-red-800 hover:underline"
                    >
                      Permanently delete...
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modal && <AdminModal admin={modal.id ? modal : null} currentUserId={user?.id} onSave={() => { setModal(null); loadAdmins(); }} onClose={() => setModal(null)} />}
      
      {deleteModal && (
        <ConfirmDeleteModal
          name={deleteModal.name}
          type="Admin"
          onConfirm={() => permanentlyDeleteAdmin(deleteModal.id)}
          onClose={() => setDeleteModal(null)}
        />
      )}

      {inviteModal && (
        <InviteModal
          admin={inviteModal}
          onClose={() => setInviteModal(null)}
        />
      )}
    </div>
  );
}
